/**
 * Thin API client for the public-jobs endpoints.
 *
 * Centralizing the base URL + fetch calls here keeps the components free
 * of `http://127.0.0.1:5000/api/public/...` strings and makes it trivial
 * to swap in a different env.
 */

/**
 * Backend base URL — single source of truth from src/config/api.js, which
 * handles localhost / staging / production hostnames and the
 * REACT_APP_API_URL build-time override. Keeps this client consistent with
 * the rest of the app (the recruiter/company dashboards use the same base).
 */
import { API_BASE_URL } from '@/config/api';

const API_BASE = API_BASE_URL;

async function jsonOrThrow(res) {
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) {
    const err = new Error((body && body.error) || res.statusText || 'Request failed');
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export async function listCompanies() {
  const res = await fetch(`${API_BASE}/api/public/companies`, {
    credentials: 'include',
  });
  return jsonOrThrow(res);
}

export async function listAllJobs({ q = '', company = '' } = {}) {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  if (company) qs.set('company', company);
  const res = await fetch(`${API_BASE}/api/public/jobs?${qs.toString()}`, {
    credentials: 'include',
  });
  return jsonOrThrow(res);
}

export async function getCompanyPage(slug) {
  const res = await fetch(
    `${API_BASE}/api/public/companies/${encodeURIComponent(slug)}`,
    { credentials: 'include' },
  );
  return jsonOrThrow(res);
}

export async function getJobDetail(jobId) {
  const res = await fetch(
    `${API_BASE}/api/public/jobs/${encodeURIComponent(jobId)}`,
    { credentials: 'include' },
  );
  return jsonOrThrow(res);
}

export async function checkApplied(jobId) {
  const res = await fetch(
    `${API_BASE}/api/public/jobs/${encodeURIComponent(jobId)}/check-applied`,
    { credentials: 'include' },
  );
  return jsonOrThrow(res);
}

/**
 * Submit application. `payload` is a plain object — we wrap it in
 * FormData here so the optional resume file rides along correctly.
 */
export async function submitApplication(jobId, payload) {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') fd.append(k, v);
  });
  const res = await fetch(
    `${API_BASE}/api/public/jobs/${encodeURIComponent(jobId)}/apply`,
    { method: 'POST', credentials: 'include', body: fd },
  );
  return jsonOrThrow(res);
}

/** Lightweight "who am I" check used by the public navbar. */
export async function getMe() {
  const res = await fetch(`${API_BASE}/api/public/me`, {
    credentials: 'include',
  });
  return jsonOrThrow(res);
}

export async function logoutCandidate() {
  const res = await fetch(`${API_BASE}/api/public/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  clearCandidateAuth();
  return jsonOrThrow(res);
}

// ---- Auth (uses the existing endpoints — no new backend) ----------------

// The candidate session cookie is third-party and dropped cross-domain, so we
// persist the signed token the backend returns and let the global interceptor
// attach it as X-User-Token. Mirrors the employer/admin/recruiter token flow.
function storeCandidateAuth(body) {
  try {
    if (body && body.user_token) {
      localStorage.setItem('user_token', body.user_token);
      sessionStorage.setItem('user_token', body.user_token);
    }
    if (body && body.user_email) {
      localStorage.setItem('user_email', body.user_email);
      sessionStorage.setItem('user_email', body.user_email);
    }
  } catch (e) {
    /* storage may be unavailable (private mode) — ignore */
  }
}

function clearCandidateAuth() {
  try {
    ['user_token', 'user_email'].forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch (e) {
    /* ignore */
  }
}

export async function loginCandidate({ email, password }) {
  const res = await fetch(`${API_BASE}/api/user-login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const body = await jsonOrThrow(res);
  storeCandidateAuth(body);
  return body;
}

export async function signupCandidate({ name, email, password, resume, skills }) {
  // signup-apply is multipart/form-data — name, email, password, resume, skills
  const fd = new FormData();
  fd.append('name', name);
  fd.append('email', email);
  fd.append('password', password);
  if (skills) fd.append('skills', skills);
  if (resume) fd.append('resume', resume);
  const res = await fetch(`${API_BASE}/api/signup-apply`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const body = await jsonOrThrow(res);
  storeCandidateAuth(body);
  return body;
}
